import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { COMPANY_VARS } from '../utils/legal';

/**
 * A long-form legal page (Terms of Use, Privacy Policy). Copy lives under pages.<page>.
 * A section names its keys: title and text, then optional list items, a paragraph after the list
 * and a closing paragraph. `link` follows the text with an internal link; `mail` puts the contact
 * email at the end of the text or of the paragraph after the list.
 */
export const LegalDoc = ({ page, sections, hasClosing = false }) => {
  const { t } = useTranslation();
  const copy = (name) => t(`pages.${page}.${name}`, COMPANY_VARS);
  const companyMail = t('companyMail');

  const mailLink = (
    <>
      <a href={`mailto:${companyMail}`}>{companyMail}</a>.
    </>
  );

  return (
    <div className="mt-root mt-root--page mt-doc">
      <article className="mt-doc__body">
        <Link className="mt-doc__back" to="/">
          <ArrowLeft aria-hidden="true" />
          {t('appName')}
        </Link>
        <h1 className="mt-doc__title">{copy('title')}</h1>
        <p className="mt-2 text-[15px] text-[color:var(--mt-ink-2)]">
          {copy('updated')}
        </p>
        <p className="mt-doc__lead">{copy('intro')}</p>

        {sections.map((section) => (
          <section key={section.title}>
            <h2>{copy(section.title)}</h2>
            <p>
              {copy(section.text)}
              {section.link && (
                <>
                  {' '}
                  <Link to={section.link.to}>{copy(section.link.label)}</Link>.
                </>
              )}
              {section.mail === 'text' && mailLink}
            </p>
            {section.items && (
              // The reset strips list markers and mt-doc doesn't restore them
              <ul className="list-disc">
                {section.items.map((item) => (
                  <li key={item}>{copy(item)}</li>
                ))}
              </ul>
            )}
            {section.after && (
              <p>
                {copy(section.after)}
                {section.mail === 'after' && mailLink}
              </p>
            )}
            {section.end && <p>{copy(section.end)}</p>}
          </section>
        ))}

        {hasClosing && <p className="mt-doc__closing">{copy('closing')}</p>}
        <p className="mt-doc__company">
          {t(`pages.${page}.companyStatement`, {
            ...COMPANY_VARS,
            appName: t('appName'),
          })}
        </p>
      </article>
    </div>
  );
};
